import { t } from "@bezbolisno/i18n";
import { matchesQuery } from "@bezbolisno/kit/person-name";
import { Card } from "@bezbolisno/ui/card";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { z } from "zod";
import { PageHeader } from "../../../components/page-header";
import { Pager } from "../../../components/pager";
import { PatientCard } from "../../../screens/patients/patient-card";
import { type RailPatient, railQuery } from "../../../screens/patients/queries";
import { SearchControls } from "../../../screens/patients/search-controls";

const Search = z.object({
  q: z.string().optional(),
  view: z.string().optional(),
  page: z.coerce.number().int().optional(),
  pageSize: z.coerce.number().int().optional(),
});

function matchView(p: RailPatient, view: string): boolean {
  if (view === "archived") return p.status === "archived";
  if (p.status === "archived") return false; // archived live only in their own view
  if (view === "active") return p.status === "active";
  if (view === "paused") return p.status === "paused" || p.status === "maintenance";
  if (view === "action") return p.hasOpenTask;
  if (view === "event") return p.nextStart != null;
  return true;
}

/** The patients list: the rail read model filtered by `?view=` and `?q=`, paged client-side. */
export const Route = createFileRoute("/_authed/patients/")({
  validateSearch: Search,
  loader: ({ context }) => context.queryClient.ensureQueryData(railQuery),
  component: PatientsPage,
});

function PatientsPage() {
  const sp = Route.useSearch();
  const view = sp.view ?? "all";
  const q = sp.q ?? "";
  const { data: rail, isError } = useQuery(railQuery);

  // Same rule as the API's search: every token, any order, any apostrophe.
  const patients = (rail?.patients ?? []).filter(
    (p) => matchView(p, view) && matchesQuery([p.name, p.sport, p.episodeTitle], q),
  );

  // Clinic-scale list — page the already-filtered array (same clamps as tasks).
  const pageSize = Math.min(100, Math.max(5, sp.pageSize || 12));
  const pages = Math.max(1, Math.ceil(patients.length / pageSize));
  const page = Math.min(pages, Math.max(1, sp.page || 1));
  const pageItems = patients.slice((page - 1) * pageSize, page * pageSize);
  const spStrings: Record<string, string | undefined> = {
    q: sp.q,
    view: sp.view,
    page: sp.page != null ? String(sp.page) : undefined,
    pageSize: sp.pageSize != null ? String(sp.pageSize) : undefined,
  };

  return (
    <div className="fade-in">
      <PageHeader
        title={t.nav.patients}
        actions={
          <Link to="/patients/new" className="btn btn--primary">
            + {t.patientForm.add}
          </Link>
        }
      />
      <SearchControls view={view} q={sp.q ?? ""} />
      {isError ? (
        <Card className="p-5 text-sm text-[color:var(--muted)]">{t.common.loadError}</Card>
      ) : patients.length === 0 ? (
        <Card className="p-5 text-[color:var(--muted)]">{t.shell.noneInFilter}</Card>
      ) : (
        <>
          <div className="plist" style={{ marginTop: 4 }}>
            {pageItems.map((p) => (
              <PatientCard key={p.id} p={p} />
            ))}
          </div>
          {patients.length > pageSize ? (
            <Pager
              basePath="/patients"
              sp={spStrings}
              page={page}
              pageSize={pageSize}
              total={patients.length}
            />
          ) : null}
        </>
      )}
    </div>
  );
}
